import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import {
  estimateAuditCost,
  resolveEntitlement,
  type AuditType,
} from '@traaker/shared';
import { useCredits } from '../../contexts/CreditContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatNumber } from '../../i18n/format';

/**
 * Agency-only pre-action credit cost preview, shown next to a "Run audit" CTA.
 * Renders nothing for non-agency tenants (credits don't apply). The estimate is
 * a guide ("≈"): website/earned-media reserve the tier MAX (pages/fetch) and are
 * trued-up at commit; brand axes use prompts × the tier engine count. Turns amber
 * when the estimate exceeds the available balance.
 *
 * Props the caller knows:
 *   - auditType
 *   - promptCount (brand axes) — number of active prompts that will run
 *   - newsQueryCount (news_watch)
 *   - competitorCount (brand_referral) — tracked competitors (matrix probe)
 * Pages/fetch are resolved from the tenant's tier scalars here (the caller
 * rarely knows them).
 */
export default function CostEstimate({
  auditType,
  promptCount,
  newsQueryCount,
  competitorCount,
  maxPagesOverride,
  maxFetchOverride,
}: {
  auditType: AuditType;
  promptCount?: number;
  newsQueryCount?: number;
  competitorCount?: number;
  /** Per-brand agency plafond (brand.auditConfig.maxPagesPerWebsiteAudit) — wins over the tier scalar. */
  maxPagesOverride?: number;
  /** Per-brand agency plafond (brand.auditConfig.maxFetchPerEarnedMedia) — wins over the tier scalar. */
  maxFetchOverride?: number;
}) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { isAgency, balance } = useCredits();
  if (!isAgency || !tenant) return null;

  const tier = tenant.tier;
  const overrides = tenant.settings?.quotaOverrides;
  const packs = tenant.settings?.activePacks;
  const ent = (metric: string) => resolveEntitlement(metric, tier, {
    override: (overrides as any)?.[metric],
    packs: packs?.[metric],
  });

  // Tier engine count (allowlist size). Slight over-estimate for authority
  // (which excludes OpenAI) — fine for a preview.
  const engineCount = ent('engines').allowed?.length ?? 0;

  // Page/fetch budgets must mirror the AGENT's real ceiling (Trigger Lambda
  // resolvePagesPerAudit / resolveFetchPerEarnedMedia), NOT the registry scalar's
  // limit — which for agency clamps to the systemCeiling (pages 500 / fetch 300),
  // 5× the real budget. The agent caps website pages at 100 and earned-media
  // fetches at 100 for the uncapped tiers.
  const AGENT_PAGE_BUDGET = 100;
  const AGENT_FETCH_BUDGET = 100;
  const pagesScalar = ent('pages_per_website_audit').limit;
  const tierMaxPages = pagesScalar === -1 ? AGENT_PAGE_BUDGET : Math.min(pagesScalar, AGENT_PAGE_BUDGET);
  const fetchScalar = ent('fetch_per_earned_media').limit;
  const tierMaxFetch = fetchScalar === -1 ? AGENT_FETCH_BUDGET : Math.min(fetchScalar, AGENT_FETCH_BUDGET);
  // A per-brand plafond (agency) wins over the tier scalar — mirrors the Trigger
  // Lambda, which prefers brand.auditConfig.maxPages…/maxFetch… when set.
  const maxPages =
    typeof maxPagesOverride === 'number' && maxPagesOverride > 0 ? maxPagesOverride : tierMaxPages;
  const maxFetch =
    typeof maxFetchOverride === 'number' && maxFetchOverride > 0 ? maxFetchOverride : tierMaxFetch;

  const { total, breakdown } = estimateAuditCost(auditType, {
    promptCount,
    engineCount,
    maxPages,
    maxFetch,
    newsQueryCount,
    competitorCount,
  });

  if (total <= 0) return null;

  const insufficient = balance ? balance.available < total : false;
  const isMax = auditType === 'website' || auditType === 'earned_media';

  // Itemized breakdown so an agency sees WHY a referral run (engine calls +
  // competitors) costs more than a narrative run. Falls back to a single line.
  const PRIMITIVE_LABEL: Record<string, string> = {
    engine_call: t('costEstimate.primitiveEngineCall'),
    page_analyzed: t('costEstimate.primitivePages'),
    fetch: t('costEstimate.primitiveFetch'),
    news_query: t('costEstimate.primitiveNewsQuery'),
    competitor: t('costEstimate.primitiveCompetitor'),
    report_export: t('costEstimate.primitiveReport'),
    product_review: t('costEstimate.primitiveProductReview'),
  };
  const lines = Object.entries(breakdown)
    .filter(([, n]) => (n ?? 0) > 0)
    .map(([prim, n]) => `${formatNumber(n!)} ${PRIMITIVE_LABEL[prim] ?? prim}`);
  const detail = isMax ? t('costEstimate.detailMax') : t('costEstimate.detailEstimate');
  const tooltip = lines.length > 1 ? t('costEstimate.tooltipDetail', { detail, lines: lines.join(' + ') }) : detail;

  return (
    <Tooltip title={tooltip}>
      <Chip
        icon={<BoltIcon fontSize="small" />}
        label={`${isMax ? '≤ ' : '≈ '}${formatNumber(total)} ${t('costEstimate.credits')}`}
        size="small"
        color={insufficient ? 'warning' : 'default'}
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    </Tooltip>
  );
}
